/**
 * Event Call to Action Service
 * Handles CTA CRUD operations, URL building, and QR code generation
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import type { Event } from "../types/event.types";
import type {
  EventCallToAction,
  CreateEventCTAInput,
  UpdateEventCTAInput,
  CTAType,
} from "../types/event.types";
import {
  ctaOmitsDiscount,
  ctaUsesClassroomLink,
  toBaseVariant,
  type CTABaseVariant,
} from "./ctaClassroom";
export {
  ctaLinkVariant,
  ctaOmitsDiscount,
  ctaUsesClassroomLink,
} from "./ctaClassroom";
import {
  INSURANCE_CLASSROOM_CTA_BASE_URL,
  SUBSCRIPTION_CTA_BASE_URL,
} from "../types/event.types";
// R2B (2026-05-17): shared QR composite generator (QR + rep name + org/event
// label) used by both event and org CTA services. Audit must-fix: avoid
// duplicating overlay code between event and org CTA paths.
import { generateCompositeQRSVG } from "./ctaQRComposite.service";

// Re-export for use in other components
export { SUBSCRIPTION_CTA_BASE_URL };

// Storage bucket for CTA QR codes
const CTA_QR_BUCKET = "event-cta-qr-codes";

// =====================================================
// CTA CRUD OPERATIONS
// =====================================================

/**
 * Get all ACTIVE CTAs for an event. A switched-off CTA (is_active = false, migration 2026-10-29-1260 -- the generic
 * CTAs of classes, tables, seminars and speaking engagements) is kept for history but never listed, counted (tab
 * badge), copied (duplicate modal) or regenerated.
 */
export async function getEventCTAs(
  eventId: UUID,
): Promise<EventCallToAction[]> {
  const { data, error } = await supabase
    .from("event_call_to_actions")
    .select(
      `*, sales_position:sales_positions(id, title, assigned_name, is_active)`,
    )
    .eq("event_id", eventId)
    .eq("is_active", true)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Error fetching CTAs:", error);
    throw error;
  }

  return data || [];
}

/**
 * Get a single CTA by ID
 */
export async function getEventCTA(
  ctaId: UUID,
): Promise<EventCallToAction | null> {
  const { data, error } = await supabase
    .from("event_call_to_actions")
    .select("*")
    .eq("id", ctaId)
    .single();

  if (error) {
    console.error("Error fetching CTA:", error);
    throw error;
  }

  return data;
}

/**
 * Create a new CTA
 */
export async function createEventCTA(
  input: CreateEventCTAInput,
  userId: UUID,
): Promise<EventCallToAction> {
  const { data, error } = await supabase
    .from("event_call_to_actions")
    .insert({
      event_id: input.event_id,
      name: input.name,
      cta_type: input.cta_type,
      base_url: input.base_url,
      parameters: input.parameters,
      // Reward configuration
      reward_type: input.reward_type || "none",
      reward_description: input.reward_description || null,
      voucher_amount: input.voucher_amount || null,
      voucher_start_date: input.voucher_start_date || null,
      voucher_end_date: input.voucher_end_date || null,
      voucher_expiry_days: input.voucher_expiry_days ?? 30,
      voucher_expires_at_event_end: input.voucher_expires_at_event_end ?? false,
      // Sales rep attribution
      sales_position_id: input.sales_position_id || null,
      // URL configuration
      include_state_param: input.include_state_param !== false,
      // Opt-in flags are written explicitly: a field missing from this insert silently falls back to
      // the column default, which is how "Use State Defaults" was lost on every newly created CTA.
      use_state_defaults: input.use_state_defaults === true,
      single_voucher: input.single_voucher === true,
      use_member_signup: input.use_member_signup || false,
      display_away_plans: input.display_away_plans || false,
      created_by: userId,
      updated_by: userId,
    })
    .select(
      `*, sales_position:sales_positions(id, title, assigned_name, is_active)`,
    )
    .single();

  if (error) {
    console.error("Error creating CTA:", error);
    throw error;
  }

  return data;
}

/**
 * Update an existing CTA
 */
export async function updateEventCTA(
  ctaId: UUID,
  updates: UpdateEventCTAInput,
  userId: UUID,
): Promise<EventCallToAction> {
  const { data, error } = await supabase
    .from("event_call_to_actions")
    .update({
      ...updates,
      updated_by: userId,
      updated_at: new Date().toISOString(),
    })
    .eq("id", ctaId)
    .select()
    .single();

  if (error) {
    console.error("Error updating CTA:", error);
    throw error;
  }

  return data;
}

/**
 * Delete a CTA
 */
export async function deleteEventCTA(ctaId: UUID): Promise<void> {
  // Round-4 audit (a49a5946): the QR asset used to be removed BEFORE the row delete was known to have
  // happened, so a failed delete left a live CTA pointing at a missing image. Order is now: read the
  // asset path -> delete the ROW and confirm it -> only then remove the asset. On an ambiguous result
  // (no server answer) the row is re-checked, and the asset is kept unless the row is confirmed gone.
  const { data: cta } = await supabase
    .from("event_call_to_actions")
    .select("qr_code_url")
    .eq("id", ctaId)
    .maybeSingle();

  const { data: deleted, error } = await supabase
    .from("event_call_to_actions")
    .delete()
    .eq("id", ctaId)
    .select("id");

  let rowGone: boolean;
  if (error) {
    if ((error as { code?: string }).code) {
      // An explicit server refusal: nothing was deleted; the asset stays with its row.
      console.error("Error deleting CTA:", error);
      throw error;
    }
    // No server answer: find out whether the delete happened before touching the asset.
    const { data: still, error: checkError } = await supabase
      .from("event_call_to_actions")
      .select("id")
      .eq("id", ctaId)
      .maybeSingle();
    rowGone = !checkError && !still;
    if (!rowGone) {
      throw error; // the row is still there, or we can't tell: keep its QR asset
    }
  } else {
    rowGone = (deleted ?? []).length === 1;
    if (!rowGone) {
      // RLS filtered the delete (or the row was already gone): don't touch an asset we can't account for.
      throw new Error("The CTA couldn't be deleted.");
    }
  }

  if (rowGone && cta?.qr_code_url) {
    try {
      const fileName = extractFileNameFromUrl(cta.qr_code_url);
      if (fileName) {
        await supabase.storage.from(CTA_QR_BUCKET).remove([fileName]);
      }
    } catch (err) {
      // The row is gone; a leftover file is harmless litter.
      console.warn("Failed to delete QR code from storage:", err);
    }
  }
}

// =====================================================
// URL BUILDING
// =====================================================

/**
 * Build the full URL for a CTA based on its type and parameters
 * For subscription CTAs, always uses the environment-configured base URL
 *
 * Auto-populated parameters for subscription CTAs:
 * - ste: Event state (2-char code)
 * - ctaid: CTA UUID (for generate-offer API to look up CTA config)
 * - discountvalue: Voucher amount as numeric value (e.g., 20 for $20)
 *
 * User-defined parameters: affid, promo, altprice, utm_source, utm_medium, utm_campaign, utm_term, utm_content
 */
export function buildCTAUrl(
  cta: {
    id?: UUID; // CTA ID - used for ctaid parameter
    cta_type: CTAType;
    base_url: string;
    parameters: Record<string, string>;
    voucher_amount?: number | null; // Voucher discount value
    include_state_param?: boolean; // When false, omit ste= parameter
    /** When true the URL carries usestatedefaults=1. Defaults OFF for backwards compatibility. */
    use_state_defaults?: boolean;
    /** When true AND the reward is a voucher or offer, the URL carries singlevoucher=1. Defaults OFF. */
    single_voucher?: boolean;
    /** Needed for single_voucher: the flag applies to voucher/offer CTAs only. */
    reward_type?: string | null;
    use_member_signup?: boolean; // When true, use /member-signup/ instead of /voucher/
    display_away_plans?: boolean; // When true, add plantype=away to URL
  },
  event: {
    id: UUID;
    state?: string;
    /** From the database (render snapshot / event_cta_base_variant). 'insurance' = venue in an insurance state: no
     * discountvalue. 'classroom' = also a gun show / range night: the classroom page. Absent or 'standard' = as before. */
    cta_base_variant?: CTABaseVariant | null;
  },
): string {
  const params = new URLSearchParams();

  // For subscription CTAs, always use the environment-configured URL
  // This ensures QA uses QA URL and Production uses Production URL
  let baseUrl: string;

  if (cta.cta_type === "subscription") {
    // Insurance-state venue (migration 2026-10-09-410 rev 2, owner 2026-10-09): NO discount amount; a gun show /
    // range night there also links to the classroom page. Same rule as the database's _eib_cta_link_variant;
    // publish_event_cta_qr refuses any other form for these CTAs.
    const classroom = ctaUsesClassroomLink(cta, event.cta_base_variant);
    const omitDiscount = ctaOmitsDiscount(cta, event.cta_base_variant);
    // Use member-signup URL if bypass checkbox is checked
    if (cta.use_member_signup) {
      baseUrl = "https://ww2.uslawshield.com/member-signup/";
    } else if (classroom) {
      baseUrl = INSURANCE_CLASSROOM_CTA_BASE_URL;
    } else {
      // Always use environment variable for subscription CTAs
      baseUrl = SUBSCRIPTION_CTA_BASE_URL;
    }

    // Add auto-populated parameters for subscription type
    // include_state_param defaults to true for backwards compatibility
    if (event.state && cta.include_state_param !== false) {
      params.append("ste", event.state.toUpperCase());
    }
    // Use CTA ID so generate-offer can look up CTA config and event association
    if (cta.id) {
      params.append("ctaid", cta.id);
    }
    // Defense-in-depth: also pass eventid so generate-offer can resolve
    // the event even when the CTA row has been deleted (orphan-URL case
    // floating in an external system). Audit-AI 2026-05-28 R2-MR1:
    // generate-offer normalizes from body.eventId, body.eventid,
    // body.event_id, or url.searchParams.get('eventid'|'eventId'|'event_id').
    if (event.id) {
      params.append("eventid", event.id);
    }
    // Add discount value as numeric (strip formatting, e.g., $20 → 20). Never in an insurance state.
    if (!omitDiscount && cta.voucher_amount && cta.voucher_amount > 0) {
      params.append("discountvalue", String(cta.voucher_amount));
    }

    // ⭐ Opt-in only — `=== true`, not `!== false`. See the organization builder: this flag defaults
    // OFF so every existing event CTA and printed QR code keeps its current URL.
    if (cta.use_state_defaults === true) {
      params.append("usestatedefaults", "1");
    }

    // Opt-in only, and only for voucher/offer rewards (owner 2026-09-29). Mirrored by the EIB guard and
    // fingerprint (migration 2026-09-29-025), which use the same effective value.
    if (
      cta.single_voucher === true &&
      (cta.reward_type === "voucher" || cta.reward_type === "offer")
    ) {
      params.append("singlevoucher", "1");
    }

    // Add away plans parameter if checked
    if (cta.display_away_plans) {
      params.append("plantype", "away");
    }

    // Add user-defined parameters
    Object.entries(cta.parameters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
  } else {
    // For 'other' type, use the stored base_url
    baseUrl = cta.base_url;

    // Add all parameters
    Object.entries(cta.parameters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
  }

  const queryString = params.toString();
  // Remove trailing slash for consistent URL formatting
  const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;

  return queryString ? `${cleanBaseUrl}?${queryString}` : cleanBaseUrl;
}

/**
 * Which base page an event's subscription CTA links to ('classroom' for an insurance-state gun show or range night,
 * else 'standard'). The database decides (event_cta_base_variant, migration 2026-10-09-410); before that migration
 * exists this resolves to 'standard', which is what the server would also use.
 */
export async function getEventCTABaseVariant(
  eventId: UUID,
): Promise<CTABaseVariant> {
  const { data, error } = await supabase.rpc("event_cta_base_variant", {
    p_event_id: eventId,
  });
  if (error) {
    if ((error as { code?: string }).code === "PGRST202") {
      return "standard";
    }
    throw error;
  }
  return toBaseVariant(data);
}

/**
 * Get the default base URL for a CTA type
 */
export function getDefaultBaseUrl(ctaType: CTAType): string {
  if (ctaType === "subscription") {
    return SUBSCRIPTION_CTA_BASE_URL;
  }
  if (ctaType === "registration") {
    // Registration CTAs use the app's event registration page
    return `${window.location.origin}/event-register`;
  }
  return "";
}

// =====================================================
// QR CODE GENERATION
// =====================================================

// generateQRCodeSVG removed in R2B (2026-05-17). The shared composite
// generator (generateCompositeQRSVG) replaces it — it produces a QR
// PLUS the rep name + org/event identity labels in one SVG asset.
// Audit verdict Decision 3.

/**
 * Upload QR code to Supabase storage
 */
async function uploadQRCode(
  eventId: UUID,
  ctaId: UUID,
  svgContent: string,
): Promise<string> {
  const fileName = `${eventId}/${ctaId}-${Date.now()}.svg`;
  const blob = new Blob([svgContent], { type: "image/svg+xml" });

  const { error: uploadError } = await supabase.storage
    .from(CTA_QR_BUCKET)
    .upload(fileName, blob, {
      contentType: "image/svg+xml",
      cacheControl: "3600",
      upsert: true,
    });

  if (uploadError) {
    console.error("Error uploading QR code:", uploadError);
    throw new Error(`Failed to upload QR code: ${uploadError.message}`);
  }

  const { data: urlData } = supabase.storage
    .from(CTA_QR_BUCKET)
    .getPublicUrl(fileName);

  return urlData.publicUrl;
}

/**
 * Extract filename from Supabase storage URL
 */
function extractFileNameFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split("/");
    // The path format is /storage/v1/object/public/bucket-name/filename
    const bucketIndex = pathParts.indexOf(CTA_QR_BUCKET);
    if (bucketIndex !== -1 && bucketIndex < pathParts.length - 1) {
      return pathParts.slice(bucketIndex + 1).join("/");
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Generate QR code for a CTA and store it.
 *
 * R2B (2026-05-17) — audit must-fix: correct upload-first ordering to
 * avoid the bug where deleting the old QR before the new one is
 * successfully uploaded leaves the CTA pointing at a missing asset.
 * New order:
 *   1. Build URL + composite SVG (with rep + org overlay)
 *   2. Upload new SVG to storage (new filename, no collision)
 *   3. Update DB row with new URL
 *   4. Best-effort delete old file (failure here is just storage litter,
 *      not a broken UI link)
 *
 * Audit verdict Decision 3: composite uses generateCompositeQRSVG so
 * the QR shows rep name + organization beneath it. Each rep can find
 * their own QR among many without scanning each one.
 */
export async function generateCTAQRCode(
  ctaId: UUID,
  event: Event,
  userId: UUID,
): Promise<EventCallToAction> {
  // Round-2 audit (e81d01b1, HIGH): publication is CONDITIONAL on the exact inputs the QR was rendered
  // from. If the CTA or its event changed while we rendered/uploaded, the new asset is discarded and
  // we start over from a fresh read -- never write a URL built from an older row.
  for (let attempt = 1; attempt <= 3; attempt++) {
    const result = await generateCTAQRCodeOnce(ctaId, event, userId);
    if (result.status === "published") {
      return result.cta;
    }
  }
  throw new Error(
    "This CTA kept changing while its QR code was being generated. Try again.",
  );
}

async function generateCTAQRCodeOnce(
  ctaId: UUID,
  event: Event,
  _userId: UUID,
): Promise<
  { status: "published"; cta: EventCallToAction } | { status: "stale" }
> {
  // Round-3 audit (54a5ff81, HIGH): the row we render, the event state we use, and the fingerprint
  // publication will check all come from ONE server statement (one snapshot). Reading them separately
  // let an edit-then-revert publish a URL rendered from the transient row.
  const { data: snap, error: snapError } = await supabase.rpc(
    "eib_event_cta_render_snapshot",
    { p_cta_id: ctaId },
  );
  if (snapError) {
    throw snapError;
  }
  const snapshot = snap as {
    cta: EventCallToAction;
    event_state: string | null;
    base_variant?: string | null;
    fingerprint: string;
  } | null;
  if (!snapshot?.cta || !snapshot.fingerprint) {
    throw new Error("CTA not found");
  }
  const cta = snapshot.cta;
  const fingerprint = snapshot.fingerprint;
  // ste= comes from the SNAPSHOT's state, not the caller's (possibly stale) event object.
  const urlEvent = {
    ...event,
    id: cta.event_id,
    state: snapshot.event_state ?? undefined,
    // The base page comes from the same snapshot as the fingerprint publication checks.
    cta_base_variant: toBaseVariant(snapshot.base_variant),
  };

  let repName: string | null = null;
  if (cta.sales_position_id) {
    const { data: pos } = await supabase
      .from("sales_positions")
      .select("assigned_name, title, is_active")
      .eq("id", cta.sales_position_id)
      .single();
    if (pos) {
      // Per audit Decision 2: inactive reps keep attribution. Surface
      // "(inactive)" on the QR overlay so the operator at the event
      // sees the staleness signal.
      const name = pos.assigned_name || pos.title;
      repName = pos.is_active === false ? `${name} (inactive)` : name;
    }
  }

  // 2026-06-03 (Sprint A Batch 1 A10.1, audit-AI GREEN): fetch the
  // event's primary organization name for the QR's second label line.
  // Prior to this change contextLabel was hardcoded to "" since the
  // 2026-05-18 fix that stopped leaking event.title onto user-facing
  // QRs ("Test 8532" incident). Heather flagged the empty label as a
  // regression: "Rep name shows but not the partner's name. It needs
  // to show the partner that is selected under the Partner/Organization
  // name as well (not event name)." Now we use the linked organization,
  // which is the operator-curated value that belongs on a public QR.
  //
  // The composite renderer (ctaQRComposite.service.ts) truncates to 32
  // chars; we don't pre-trim. PostgREST embed normalization is
  // defensive — embed can return either array or single object
  // depending on PostgREST's relation typing (audit-AI caution).
  let contextLabel = "";
  try {
    // NOTE: there is NO `is_primary` column on event_organizations (columns are
    // id, event_id, organization_id, status, created_at, updated_at). Ordering by it made
    // PostgREST return an ERROR rather than rows, and because this destructured only
    // `data`, the error was discarded and the label silently fell back to "" for every
    // event. External audit finding #6, 2026-09-12. Order by created_at alone -- the
    // earliest association is the closest thing to "primary" that the schema supports.
    const { data: orgRow, error: orgErr } = await supabase
      .from("event_organizations")
      .select("organizations(name)")
      .eq("event_id", event.id)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    if (orgErr) {
      // Still non-fatal (an empty label is a valid composite) but no longer INVISIBLE.
      console.warn(
        "[eventCTA] promoter-label lookup failed; composite will omit the label:",
        orgErr,
      );
    }
    const orgEmbed = orgRow?.organizations as
      | { name?: string | null }
      | Array<{ name?: string | null }>
      | null
      | undefined;
    const orgName = Array.isArray(orgEmbed)
      ? (orgEmbed[0]?.name ?? "")
      : (orgEmbed?.name ?? "");
    contextLabel = (orgName ?? "").trim();
  } catch {
    // Empty label is a valid composite; the renderer omits the second
    // line cleanly. Don't fail the QR generation over a label lookup.
  }

  // UC-R2B.1 (2026-06-20): the partner is almost always set via the event's
  // Promoter/Sponsoring Organization (events.promoter_id), NOT event_organizations
  // — in QA only 1 of 32 CTA-events had an event_organizations link while 13 had a
  // promoter_id. The 2026-06-03 fix read only event_organizations, so the partner
  // label was blank on nearly every QR ("partner name missing"). Fall back to the
  // promoter org name when there's no primary linked org. Additive: an event that
  // DOES have an event_organizations primary keeps that label unchanged.
  if (!contextLabel && event.promoter_id) {
    try {
      const { data: promoterOrg } = await supabase
        .from("organizations")
        .select("name")
        .eq("id", event.promoter_id)
        .maybeSingle();
      contextLabel = (promoterOrg?.name ?? "").trim();
    } catch {
      // Leave empty — a missing label is still a valid composite.
    }
  }

  const generatedUrl = buildCTAUrl(cta, urlEvent);
  const svgContent = await generateCompositeQRSVG({
    url: generatedUrl,
    repName,
    contextLabel,
  });

  // STEP 1: upload new asset (new filename — Date.now() suffix already
  // cache-busts the URL, so no collision with old asset).
  const newQrUrl = await uploadQRCode(cta.event_id, ctaId, svgContent);

  // STEP 2: publish -- only if the CTA/event inputs are still exactly the ones this QR encodes. The RPC
  // returns the saved row, so nothing else can fail after a successful publication (round-3 audit:
  // a failed follow-up read used to delete the asset that had just been published).
  const oldQrUrl = cta.qr_code_url;
  const discardUpload = async () => {
    const orphan = extractFileNameFromUrl(newQrUrl);
    if (orphan) {
      try {
        await supabase.storage.from(CTA_QR_BUCKET).remove([orphan]);
      } catch (cleanupErr) {
        console.warn(
          "Failed to clean up an unpublished QR upload:",
          cleanupErr,
        );
      }
    }
  };
  // A transport error from this call is simply thrown: it can happen AFTER the server committed, and
  // deleting a published asset breaks the saved QR. An unreferenced file is harmless litter (the same
  // stance as the old-asset cleanup below); only an explicit server refusal deletes the upload.
  const { data: pubData, error: pubError } = await supabase.rpc(
    "publish_event_cta_qr",
    {
      p_cta_id: ctaId,
      p_fingerprint: fingerprint,
      p_generated_url: generatedUrl,
      p_qr_code_url: newQrUrl,
    },
  );
  if (pubError) {
    throw pubError;
  }
  const pub = pubData as { status?: string; cta?: EventCallToAction } | null;
  if (pub?.status !== "published" || !pub.cta) {
    await discardUpload();
    if (pub?.status === "not_found") {
      throw new Error("CTA not found");
    }
    return { status: "stale" };
  }
  const updatedCTA = pub.cta;

  // STEP 3: best-effort delete old asset. Failure here is just storage
  // litter, not user-visible breakage — the new URL is already live.
  if (oldQrUrl && oldQrUrl !== newQrUrl) {
    try {
      const oldFileName = extractFileNameFromUrl(oldQrUrl);
      if (oldFileName) {
        await supabase.storage.from(CTA_QR_BUCKET).remove([oldFileName]);
      }
    } catch (err) {
      console.warn("Failed to delete old QR code (orphan only):", err);
    }
  }

  return { status: "published", cta: updatedCTA };
}

// =====================================================
// VALIDATION
// =====================================================

/**
 * Validate subscription CTA parameters
 * Returns array of missing required fields
 */
export function validateSubscriptionCTAParams(
  params: Record<string, string>,
): string[] {
  const requiredFields = [
    { key: "affid", label: "Promoter Affiliate ID" },
    { key: "promo", label: "Promo" },
    { key: "altprice", label: "Alt Price" },
    { key: "utm_source", label: "UTM Source" },
    { key: "utm_term", label: "UTM Term" },
    { key: "utm_campaign", label: "UTM Campaign" },
    { key: "utm_medium", label: "UTM Medium" },
  ];

  const missing: string[] = [];
  for (const field of requiredFields) {
    if (!params[field.key] || params[field.key].trim() === "") {
      missing.push(field.label);
    }
  }

  return missing;
}

/**
 * Validate other CTA (requires base URL)
 */
export function validateOtherCTA(baseUrl: string): boolean {
  if (!baseUrl || baseUrl.trim() === "") {
    return false;
  }
  try {
    new URL(baseUrl);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if event has required state for subscription CTAs
 */
export function eventHasRequiredState(event: Event): boolean {
  return Boolean(event.state && event.state.length === 2);
}

/**
 * Sprint A Batch 2 Ship D (audit-AI GREEN): regenerate QR composites
 * for every CTA on an event. Use case: CTAs created before A10.1
 * shipped (2026-06-03) have QR composites with empty contextLabel.
 * Running this re-renders each CTA's composite through the current
 * generateCTAQRCode path, picking up the partner-org label.
 *
 * Sequential loop (NOT parallel) to avoid hammering the QR backend
 * concurrency limit. Soft-deleted CTAs are skipped (they wouldn't
 * gain anything from a regenerated QR). Per-CTA failures are
 * collected in the errors array — the loop continues so a single
 * failure doesn't abort the batch.
 */
/**
 * Phase 1.1 Ship 3 Layer 3 helper.
 *
 * After create_event / duplicate_event mint the generic CTA via the
 * DB trigger, this service fetches the freshly-created row(s) and
 * runs the existing generateCTAQRCode pipeline to populate
 * generated_url + qr_code_url. Skips events whose generic CTA
 * already has both URLs (idempotent — Layer 4 admin repair calls
 * the same helper).
 *
 * Per auditor R1 must-have #2: types were regenerated when
 * is_system_generic landed (see crm-frontend/src/types/database.types.ts).
 */
export async function regenerateGenericCTAQRs(
  eventIds: UUID[],
  userId: UUID,
  onProgress?: (current: number, total: number) => void,
): Promise<{
  regeneratedTotal: number;
  failedTotal: number;
  skippedTotal: number;
  perEvent: Array<{
    eventId: UUID;
    ctaId?: UUID;
    skipped?: boolean;
    error?: string;
  }>;
}> {
  const { getEventById } = await import("./events.service");

  const perEvent: Array<{
    eventId: UUID;
    ctaId?: UUID;
    skipped?: boolean;
    error?: string;
  }> = [];
  let regeneratedTotal = 0;
  let failedTotal = 0;
  let skippedTotal = 0;

  for (let i = 0; i < eventIds.length; i++) {
    const eventId = eventIds[i];
    try {
      const { data: cta, error: ctaErr } = await supabase
        .from("event_call_to_actions")
        .select("id, generated_url, qr_code_url")
        .eq("event_id", eventId)
        .eq("is_system_generic", true)
        .eq("is_active", true) // a switched-off generic CTA (migration 1260) is never redrawn
        .maybeSingle();
      if (ctaErr) {
        throw new Error(ctaErr.message);
      }
      if (!cta) {
        // No generic CTA for this event (e.g., subscription_signup_eligible
        // was false). Not an error; skip.
        perEvent.push({ eventId, skipped: true });
        skippedTotal += 1;
        continue;
      }
      if (cta.generated_url && cta.qr_code_url) {
        // Already has both URLs — idempotent skip
        perEvent.push({ eventId, ctaId: cta.id, skipped: true });
        skippedTotal += 1;
        continue;
      }

      const event = await getEventById(eventId);
      if (!event) {
        throw new Error("Event not found");
      }
      await generateCTAQRCode(cta.id, event as Event, userId);
      perEvent.push({ eventId, ctaId: cta.id });
      regeneratedTotal += 1;
    } catch (err) {
      perEvent.push({
        eventId,
        error: err instanceof Error ? err.message : String(err),
      });
      failedTotal += 1;
    } finally {
      onProgress?.(i + 1, eventIds.length);
    }
  }

  return { regeneratedTotal, failedTotal, skippedTotal, perEvent };
}

/**
 * Phase 1.1 Ship 3 Layer 4 helper.
 *
 * Admin repair pass, in two steps, each reported PER EVENT (audit eb187a16 P2: "no broken QRs" alone is not done):
 *  1. QR: every event whose generic CTA lacks generated_url or qr_code_url is regenerated; and every subscription CTA
 *     (rep CTAs too) of an upcoming insurance-state event whose link is missing or still carries a discount amount
 *     (eib_cta_link_repair_candidates, migration 2026-10-09-410 rev 2; owner 2026-10-09).
 *  2. Flyers: every LIVE flyer of an upcoming event that no longer matches its CTA is replaced -- chosen by the server
 *     (eib_cta_flyer_repair_candidates, migration 2026-10-09-410 rev 2), which compares the flyer's recorded link and
 *     fingerprint with the CTA's current ones. It does not depend on the QR being incomplete, so a flyer whose
 *     replacement failed after its QR was rebuilt is picked up again on the next run. Never creates a new flyer.
 * Then the candidates are read again: anything still listed is reported as not done.
 */
export type RepairQRStatus = "regenerated" | "ok" | "failed" | "not_checked";
/**
 * "artwork_pending" (owner 2026-10-12): an insurance-state flyer whose event type has no insurance artwork (anything but
 * a range night or gun show). Not a failure: nothing was rendered or saved, the existing flyer (if any) is LEFT LIVE
 * (internal audit P3: it may still show an amount; the page says so and tells the admin to remove it from Files),
 * and a run after an artwork is mapped for that type replaces it. The server still lists it as stale, so the verification step counts
 * it separately (flyersArtworkPending) instead of as "still out of date".
 */
export type RepairFlyerStatus =
  | "replaced"
  | "current"
  | "failed"
  | "artwork_pending"
  | "not_checked";
export interface RepairEventResult {
  eventId: UUID;
  qr: RepairQRStatus;
  flyer: RepairFlyerStatus;
  error?: string;
}

export async function findAndRepairBrokenGenericCTAs(
  userId: UUID,
  onProgress?: (current: number, total: number) => void,
): Promise<{
  candidateEvents: number;
  regeneratedTotal: number;
  failedTotal: number;
  flyersRefreshed: number;
  flyersFailed: number;
  flyersStillStale: number;
  flyersArtworkPending: number;
  perEvent: RepairEventResult[];
}> {
  const { data: broken, error: queryErr } = await supabase
    .from("event_call_to_actions")
    .select("event_id")
    .eq("is_system_generic", true)
    .eq("is_active", true) // switched-off generic CTAs (migration 1260) are not repaired
    .or("generated_url.is.null,qr_code_url.is.null");
  if (queryErr) {
    throw new Error(queryErr.message);
  }
  const qrEventIds = (broken ?? []).map((r) => r.event_id as UUID);

  const perEvent = new Map<UUID, RepairEventResult>();
  const row = (eventId: UUID): RepairEventResult => {
    let r = perEvent.get(eventId);
    if (!r) {
      r = { eventId, qr: "ok", flyer: "current" };
      perEvent.set(eventId, r);
    }
    return r;
  };
  const addError = (r: RepairEventResult, msg: string) => {
    r.error = r.error ? `${r.error}; ${msg}` : msg;
  };

  // ---- 1. QR ----------------------------------------------------------------------------------------------------
  const qr = await regenerateGenericCTAQRs(qrEventIds, userId, onProgress);
  for (const r of qr.perEvent) {
    const out = row(r.eventId);
    if (r.error) {
      out.qr = "failed";
      addError(out, `QR not rebuilt: ${r.error}`);
    } else if (r.ctaId && !r.skipped) {
      out.qr = "regenerated";
    }
  }

  // Insurance-state subscription CTAs beyond the generic one (and any generic one the pass above did not fix).
  const { data: linkCands, error: linkErr } = await supabase.rpc(
    "eib_cta_link_repair_candidates",
  );
  if (linkErr && (linkErr as { code?: string }).code !== "PGRST202") {
    throw new Error(linkErr.message);
  }
  const rebuilt = new Set(
    qr.perEvent
      .filter((r) => r.ctaId && !r.skipped && !r.error)
      .map((r) => r.ctaId),
  );
  const pending = (
    (linkCands ?? []) as Array<{
      event_id: UUID;
      cta_id: UUID;
      reason: string;
    }>
  ).filter((c) => !rebuilt.has(c.cta_id));
  let extraRegenerated = 0;
  if (pending.length > 0) {
    const { getEventById } = await import("./events.service");
    for (const c of pending) {
      const out = row(c.event_id);
      try {
        const event = await getEventById(c.event_id);
        if (!event) {
          throw new Error("Event not found");
        }
        await generateCTAQRCode(c.cta_id, event as Event, userId);
        if (out.qr !== "failed") {
          out.qr = "regenerated";
        }
        extraRegenerated += 1;
      } catch (err) {
        out.qr = "failed";
        addError(
          out,
          `QR not rebuilt (${c.reason}): ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    }
  }

  // ---- 2. Flyers that no longer match their CTA ----------------------------------------------------------------
  const listStale = async (): Promise<Array<{
    event_id: UUID;
    cta_id: UUID;
    attachment_id: UUID;
    reason: string;
  }> | null> => {
    const { data, error } = await supabase.rpc(
      "eib_cta_flyer_repair_candidates",
    );
    if (error) {
      if ((error as { code?: string }).code === "PGRST202") {
        return null; // migration 410 rev 2 not applied yet
      }
      throw new Error(error.message);
    }
    return (data ?? []) as Array<{
      event_id: UUID;
      cta_id: UUID;
      attachment_id: UUID;
      reason: string;
    }>;
  };
  const stale = await listStale();
  let flyersRefreshed = 0;
  let flyersFailed = 0;
  // CTA ids whose insurance event type has no flyer artwork (not a range night / gun show): reported, not a failure.
  const artworkPending = new Set<UUID>();
  if (stale === null) {
    for (const r of perEvent.values()) {
      r.flyer = "not_checked";
    }
  } else if (stale.length > 0) {
    const [
      { saveCTAFlyerToEvent, flyerLabel, FlyerArtworkPendingError },
      { getEventById },
    ] = await Promise.all([
      import("./eventCTAFlyer.service"),
      import("./events.service"),
    ]);
    for (const c of stale) {
      const out = row(c.event_id);
      try {
        const [{ data: cta, error: ctaErr }, event] = await Promise.all([
          supabase
            .from("event_call_to_actions")
            .select("*")
            .eq("id", c.cta_id)
            .maybeSingle(),
          getEventById(c.event_id),
        ]);
        if (ctaErr) {
          throw new Error(ctaErr.message);
        }
        if (!cta || !event) {
          throw new Error("CTA or event not found");
        }
        // Re-reads the CTA, rebuilds a stale QR first, renders from the server snapshot, finalizes atomically.
        await saveCTAFlyerToEvent({
          cta,
          event: event as Event,
          label: flyerLabel(cta),
          userId,
        });
        if (out.flyer !== "failed") {
          out.flyer = "replaced";
        }
        flyersRefreshed += 1;
      } catch (err) {
        if (err instanceof FlyerArtworkPendingError) {
          artworkPending.add(c.cta_id);
          if (out.flyer !== "failed") {
            out.flyer = "artwork_pending";
          }
          continue;
        }
        out.flyer = "failed";
        flyersFailed += 1;
        addError(
          out,
          `flyer not replaced (${c.reason}): ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    }
  }

  // ---- 3. Verify: whatever the server still lists is NOT done (reported, and picked up by the next run) ----------
  let flyersStillStale = 0;
  if (stale !== null) {
    const after = ((await listStale()) ?? []).filter(
      (c) => !artworkPending.has(c.cta_id),
    );
    flyersStillStale = after.length;
    for (const c of after) {
      const out = row(c.event_id);
      if (out.flyer !== "failed") {
        out.flyer = "failed";
        addError(out, `flyer still out of date after repair (${c.reason})`);
      }
    }
  }

  const results = [...perEvent.values()];
  return {
    candidateEvents: results.length,
    regeneratedTotal: qr.regeneratedTotal + extraRegenerated,
    failedTotal: results.filter(
      (r) => r.qr === "failed" || r.flyer === "failed",
    ).length,
    flyersRefreshed,
    flyersFailed,
    flyersStillStale,
    flyersArtworkPending: artworkPending.size,
    perEvent: results,
  };
}

/**
 * Sprint A Batch 4 — A10.3 helper.
 * Iterate a list of event IDs (typically the new draft events
 * returned by duplicate_event) and regenerate composite QRs for
 * each event's cloned CTAs. Sequential by design — same rationale
 * as the per-event helper: avoid hammering the QR backend
 * concurrency limit.
 *
 * Per-event failures are collected; the loop continues so one
 * event's QR backend hiccup doesn't abort the batch.
 *
 * Per audit (Batch 4 R1): fetch each cloned event fresh via
 * getEventById. Don't reuse the source event object — composite
 * label rendering depends on the cloned event's own state /
 * partner_id / title.
 */
export async function regenerateQRsForEvents(
  eventIds: UUID[],
  userId: UUID,
  onProgress?: (current: number, total: number) => void,
): Promise<{
  regeneratedTotal: number;
  failedTotal: number;
  skippedTotal: number;
  perEvent: Array<{
    eventId: UUID;
    regenerated: number;
    failed: number;
    skipped: number;
    error?: string;
  }>;
}> {
  const { getEventById } = await import("./events.service");

  const perEvent: Array<{
    eventId: UUID;
    regenerated: number;
    failed: number;
    skipped: number;
    error?: string;
  }> = [];
  let regeneratedTotal = 0;
  let failedTotal = 0;
  let skippedTotal = 0;

  for (let i = 0; i < eventIds.length; i++) {
    const eventId = eventIds[i];
    try {
      const event = await getEventById(eventId);
      if (!event) {
        perEvent.push({
          eventId,
          regenerated: 0,
          failed: 0,
          skipped: 0,
          error: "Event not found",
        });
        failedTotal += 1;
        continue;
      }
      const result = await regenerateAllEventCTAQRs(
        eventId,
        event as Event,
        userId,
      );
      perEvent.push({
        eventId,
        regenerated: result.regenerated,
        failed: result.failed,
        skipped: result.skipped,
      });
      regeneratedTotal += result.regenerated;
      failedTotal += result.failed;
      skippedTotal += result.skipped;
    } catch (err) {
      perEvent.push({
        eventId,
        regenerated: 0,
        failed: 0,
        skipped: 0,
        error: err instanceof Error ? err.message : String(err),
      });
      failedTotal += 1;
    } finally {
      onProgress?.(i + 1, eventIds.length);
    }
  }

  return {
    regeneratedTotal,
    failedTotal,
    skippedTotal,
    perEvent,
  };
}

export async function regenerateAllEventCTAQRs(
  eventId: UUID,
  event: Event,
  userId: UUID,
): Promise<{
  regenerated: number;
  failed: number;
  skipped: number;
  errors: Array<{ ctaId: UUID; ctaName: string | null; error: string }>;
}> {
  const ctas = await getEventCTAs(eventId);
  const errors: Array<{ ctaId: UUID; ctaName: string | null; error: string }> =
    [];
  let regenerated = 0;
  let skipped = 0;

  for (const cta of ctas) {
    // Soft-deleted check — the column may not exist on every env, so
    // be defensive about it.
    const deletedAt = (cta as unknown as { deleted_at?: string | null })
      .deleted_at;
    if (deletedAt) {
      skipped++;
      continue;
    }
    try {
      await generateCTAQRCode(cta.id, event, userId);
      regenerated++;
    } catch (err) {
      errors.push({
        ctaId: cta.id,
        ctaName: cta.name ?? null,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  return {
    regenerated,
    failed: errors.length,
    skipped,
    errors,
  };
}
